<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import Twilio from './Twilio.vue';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import WahaWhatsapp from './WahaWhatsapp.vue';
import OfficialPartnerWhatsapp from './OfficialPartnerWhatsapp.vue';
import ZernioWhatsapp from './ZernioWhatsapp.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentAccount } = useAccount();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  TWILIO: 'twilio',
  WHATSAPP_CLOUD: 'whatsapp_cloud',
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  THREE_SIXTY_DIALOG: '360dialog',
  WAHA: 'waha',
  OFFICIAL_PARTNER: 'official_partner',
  ZERNIO: 'zernio',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const selectedProvider = computed(() => route.query.provider);

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

// Per-account Super Admin toggle — "API Não Oficial" (WAHA) is opt-in, off
// by default. See Metrica::AccountPlan#waha_unofficial_enabled?.
const wahaUnofficialEnabled = computed(
  () => currentAccount.value?.waha_unofficial_enabled === true
);

// Global WhatsApp provider gating (Metrica::Config, exposed on window.chatwootConfig).
// ChakraHQ is the incumbent and defaults ON (an unset flag == current behaviour).
// Zernio is the parallel Meta Cloud API path — PURELY GLOBAL: WHATSAPP_ZERNIO_ON
// alone gates it for EVERY account (no per-account zernio_enabled opt-in), off by
// default so the whole WhatsApp-via-Zernio feature is invisible until switched on.
// (IG/FB Zernio stay per-account via ChannelList.vue's zernio_enabled — WhatsApp
// deliberately does not.) See metrica-whatsapp-zernio-backup-study.
const chakraWhatsappEnabled = computed(
  () => window.chatwootConfig?.whatsappChakraOn !== 'false'
);
const zernioWhatsappEnabled = computed(
  () => window.chatwootConfig?.whatsappZernioOn === 'true'
);

// Prime Insight: apenas nossas opções são expostas na seleção de provedor.
// WhatsApp Cloud (Meta) e Twilio nativos ficam ocultos (não removidos —
// seus componentes/rotas continuam intactos, só não aparecem aqui).
const availableProviders = computed(() => [
  ...(wahaUnofficialEnabled.value
    ? [
        {
          key: PROVIDER_TYPES.WAHA,
          title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA'),
          description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA_DESC'),
          icon: 'i-woot-whatsapp',
        },
      ]
    : []),
  ...(chakraWhatsappEnabled.value
    ? [
        {
          key: PROVIDER_TYPES.OFFICIAL_PARTNER,
          title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.OFFICIAL_PARTNER'),
          description: t(
            'INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.OFFICIAL_PARTNER_DESC'
          ),
          icon: 'i-lucide-badge-check',
        },
      ]
    : []),
  ...(zernioWhatsappEnabled.value
    ? [
        {
          key: PROVIDER_TYPES.ZERNIO,
          title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.ZERNIO'),
          description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.ZERNIO_DESC'),
          icon: 'i-lucide-badge-check',
        },
      ]
    : []),
]);

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

// When exactly one provider is available, skip the picker and jump straight to
// it — a selection screen with a single card is just a needless click (this also
// preserves the old single-provider UX now that cards are flag-gated).
onMounted(() => {
  if (!selectedProvider.value && availableProviders.value.length === 1) {
    selectProvider(availableProviders.value[0].key);
  }
});

const shouldShowCloudWhatsapp = provider => {
  return (
    provider === PROVIDER_TYPES.WHATSAPP_MANUAL ||
    (provider === PROVIDER_TYPES.WHATSAPP && !hasWhatsappAppId.value)
  );
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex gap-6 justify-start">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <div class="px-6 py-5 rounded-2xl border border-n-weak">
        <!-- Show embedded signup if app ID is configured -->
        <div
          v-if="
            hasWhatsappAppId && selectedProvider === PROVIDER_TYPES.WHATSAPP
          "
        >
          <WhatsappEmbeddedSignup />

          <!-- Manual setup fallback option -->
          <div class="pt-6 mt-6 border-t border-n-weak">
            <I18nT
              keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
              tag="p"
              class="text-sm text-n-slate-11"
            >
              <template #link>
                <a
                  href="#"
                  class="underline text-n-brand"
                  @click.prevent="handleManualLinkClick"
                >
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
                    )
                  }}
                </a>
              </template>
            </I18nT>
          </div>
        </div>

        <!-- Show manual setup -->
        <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />

        <!-- Other providers -->
        <WahaWhatsapp v-else-if="selectedProvider === PROVIDER_TYPES.WAHA" />
        <OfficialPartnerWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.OFFICIAL_PARTNER"
        />
        <ZernioWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.ZERNIO"
        />
        <Twilio
          v-else-if="selectedProvider === PROVIDER_TYPES.TWILIO"
          type="whatsapp"
        />
        <ThreeSixtyDialogWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
        />
        <CloudWhatsapp v-else />
      </div>
    </div>
  </div>
</template>
